import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import UpgradeModal from '../UpgradeModal/UpgradeModal';

const featureMatrix = [
  { category: 'Browse & Discover', features: [
    { name: 'Browse 19,000+ amplifier models', anonymous: true, free: true, pro: true },
    { name: 'Search by manufacturer, model, or specs', anonymous: true, free: true, pro: true },
    { name: 'View detailed amp descriptions & history', anonymous: true, free: true, pro: true },
    { name: 'View amp photos & specifications', anonymous: true, free: true, pro: true },
    { name: 'View manufacturer pages & model lineups', anonymous: true, free: true, pro: true },
  ]},
  { category: 'Community', features: [
    { name: 'Browse the community news feed', anonymous: true, free: true, pro: true },
    { name: 'React to news feed posts', anonymous: false, free: true, pro: true },
    { name: 'Read discussion forums', anonymous: true, free: true, pro: true },
    { name: 'Post in discussion forums', anonymous: false, free: true, pro: true },
    { name: 'Upload photos to amp pages', anonymous: false, free: true, pro: true },
    { name: 'Community badges & recognition', anonymous: false, free: true, pro: true },
    { name: 'Submit feedback & feature requests', anonymous: false, free: true, pro: true },
  ]},
  { category: 'My Collection', features: [
    { name: 'Track amps you own & want', anonymous: false, free: '3 amps', pro: 'Unlimited' },
    { name: 'Log serial numbers, purchase price & date', anonymous: false, free: '3 amps', pro: 'Unlimited' },
    { name: 'Upload personal amp photos', anonymous: false, free: '3 amps', pro: 'Unlimited' },
    { name: 'Collection value dashboard', anonymous: false, free: true, pro: true },
    { name: 'Share collection via public link', anonymous: false, free: true, pro: true },
  ]},
  { category: 'AmpNebula Valuation™', features: [
    { name: 'AI-powered market valuation', anonymous: false, free: '1 per amp', pro: 'Unlimited' },
    { name: 'Fair market, trade-in & insurance values', anonymous: false, free: '1 per amp', pro: 'Unlimited' },
    { name: 'Real-time marketplace data integration', anonymous: false, free: '1 per amp', pro: 'Unlimited' },
    { name: 'Comparable sales citations', anonymous: false, free: '1 per amp', pro: 'Unlimited' },
    { name: 'Re-run valuations to track value over time', anonymous: false, free: false, pro: true },
    { name: 'PDF Valuation Certificate', anonymous: false, free: false, pro: true },
  ]},
  { category: 'Showcase', features: [
    { name: 'Browse community gear showcase', anonymous: true, free: true, pro: true },
    { name: 'Post your rig to the showcase', anonymous: false, free: true, pro: true },
    { name: 'Like & comment on showcase posts', anonymous: false, free: true, pro: true },
  ]},
  { category: 'Wiki Contributions', features: [
    { name: 'Suggest edits to amp descriptions', anonymous: false, free: true, pro: true },
    { name: 'Edits reviewed by moderators', anonymous: false, free: 'Always', pro: 'Initially' },
    { name: 'Earn trusted editor status (auto-approved edits)', anonymous: false, free: false, pro: true },
    { name: 'Track Changes editing experience', anonymous: false, free: true, pro: true },
  ]},
  { category: 'Wiring Studio  ⚡ Beta', features: [
    { name: 'Create schematics and wiring layouts with drag-and-drop', anonymous: false, free: '1 diagram', pro: 'Unlimited' },
    { name: 'Full component library (tubes, transformers, capacitors & more)', anonymous: false, free: true, pro: true },
    { name: 'Save & revisit your schematics', anonymous: false, free: '1 diagram', pro: 'Unlimited' },
    { name: 'Export schematic as PNG, SVG, or PDF', anonymous: false, free: false, pro: true },
  ]},
];

const CheckIcon = () => (
  <span style={{ color: '#00b894', fontSize: '1.2rem', fontWeight: '700' }}>✓</span>
);
const XIcon = () => (
  <span style={{ color: '#636e72', fontSize: '1.1rem' }}>—</span>
);
const LimitLabel = ({ text }) => (
  <span style={{ color: '#6c5ce7', fontSize: '0.85rem', fontWeight: '600' }}>{text}</span>
);

const renderCell = (value) => {
  if (value === true) return <CheckIcon />;
  if (value === false) return <XIcon />;
  return <LimitLabel text={value} />;
};

const tierConfig = {
  free: {
    label: 'Free',
    color: '#00b894',
    highlights: [
      'Browse the full amp database (19,000+ models)',
      'Search by manufacturer, model, or specs',
      'Read and post in discussion forums',
      'Track up to 3 amps in your collection',
      '1 AmpNebula Valuation™ per amp in your collection',
      'Suggest wiki edits (moderator-reviewed)',
      'Share your collection via public link',
      '1 saved Wiring Studio schematic',
    ],
    upgradeNote: 'Upgrade to Pro for unlimited collection tracking, unlimited valuations, PDF certificates, trusted editor status, unlimited Wiring Studio schematics with PNG export, and more.',
  },
  pro: {
    label: 'Pro',
    color: '#6c5ce7',
    highlights: [
      'Everything in Free, plus:',
      'Unlimited amp collection tracking',
      'Unlimited AmpNebula Valuations™ & re-runs',
      'Track value changes over time',
      'PDF Valuation Certificates',
      'Earn trusted editor status — auto-approved wiki edits',
      'Create discussion topics & upload images',
      'Verified Owner & Amp Collector badges',
      'Unlimited Wiring Studio schematics + PNG export',
    ],
    upgradeNote: null,
  },
  business: {
    label: 'Business',
    color: '#e17055',
    highlights: [
      'Everything in Pro, plus:',
      'API access & integrations',
      'Priority support',
      'Advanced analytics',
    ],
    upgradeNote: null,
  },
};

const PublicInfoScreen = () => {
  const { isAuthenticated, user } = useAuth();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const navigate = useNavigate();
  const collectionsRef = useRef(null);
  const showcaseRef = useRef(null);
  const newsFeedRef = useRef(null);
  const valuationRef = useRef(null);
  const certifyRef = useRef(null);
  const wikiRef = useRef(null);
  const wiringStudioRef = useRef(null);

  // Helper to scroll the main content container (not window, since the app uses overflow on .main-content)
  const scrollToTop = () => {
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      mainContent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    // Always start at the top when this page mounts (e.g. after logout)
    const mainContent = document.querySelector('.main-content');
    if (mainContent) mainContent.scrollTop = 0;
  }, []);

  useEffect(() => {
    const mainContent = document.querySelector('.main-content');
    if (!mainContent) return;
    const handleScroll = () => setShowBackToTop(mainContent.scrollTop > 400);
    mainContent.addEventListener('scroll', handleScroll, { passive: true });
    return () => mainContent.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      padding: '2rem'
    }}>
      {/* Floating Back to Top button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          title="Back to top"
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '80px',
            zIndex: 9997,
            background: 'linear-gradient(135deg, #2d1b69 0%, #6c5ce7 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '48px',
            height: '48px',
            fontSize: '1.3rem',
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(108, 92, 231, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'opacity 0.2s, transform 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          ↑
        </button>
      )}
      <div style={{
        maxWidth: '1100px',
        margin: '0 auto'
      }}>
        {/* Hero Section */}
        <div style={{
          background: 'linear-gradient(135deg, #2d1b69 0%, #11998e 100%)',
          borderRadius: '12px',
          padding: '3rem',
          marginBottom: '2rem',
          textAlign: 'center',
          color: 'white'
        }}>
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: '1rem' }}>
            <img
              src="/AmpNebulaSmall2.jpg"
              alt="AmpNebula"
              style={{
                width: '100%',
                maxWidth: '480px',
                height: 'auto',
                borderRadius: '10px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
              }}
            />
            <span style={{
              position: 'absolute',
              top: '22%',
              right: '0.5%',
              fontSize: '0.6rem',
              color: 'rgba(255,255,255,0.85)',
              fontWeight: '600',
              textShadow: '0 1px 3px rgba(0,0,0,0.5)',
              lineHeight: 1
            }}>™</span>
          </div>
          <p style={{
            fontSize: '1.15rem',
            margin: '0 0 0.5rem 0',
            opacity: 0.95,
            lineHeight: '1.6'
          }}>
            The encyclopedia of guitar &amp; bass amplifiers
          </p>
          <p style={{
            fontSize: '0.95rem',
            margin: 0,
            opacity: 0.75
          }}>
            Browse, collect, value, and discuss thousands of amplifiers — all in one place.
          </p>
        </div>

        {/* What is AmpNebula */}
        <div style={{
          background: 'white',
          borderRadius: '10px',
          padding: '2.5rem',
          border: '1px solid #e9ecef',
          marginBottom: '2rem'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#333',
            margin: '0 0 1rem 0',
            textAlign: 'center'
          }}>
            What is AmpNebula?
          </h2>
          <p style={{
            color: '#555',
            fontSize: '1.05rem',
            lineHeight: '1.7',
            marginBottom: '1.5rem',
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto 1.5rem auto'
          }}>
            AmpNebula was created by amp builders and hobbyists as an R&amp;D resource
            and hang-out place for everyone passionate about guitar amps.
            It is also one of the most comprehensive databases of guitar and bass amplifiers on the web,
            with over <strong>19,000 amp models</strong> from <strong>1,300+ manufacturers</strong>,
            detailed descriptions, community discussions, and instant market valuation with
            <strong> AmpNebula Valuation™</strong>.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1rem',
            marginTop: '1.5rem'
          }}>
            {[
              { icon: '🔍', title: 'Explore', desc: 'Browse 19,000+ amps with detailed specs, history, photos, and in-depth descriptions', link: '/amplifier_search' },
              { icon: '📦', title: 'Collect', desc: 'Track the amps you own and want with serial numbers, photos, and purchase history', scrollTo: collectionsRef },
              { icon: '💰', title: 'Value', desc: 'Get instant market valuations backed by live marketplace data', scrollTo: valuationRef },
              { icon: '💬', title: 'Discuss', desc: 'Join per-amp discussion forums with a community of collectors and players', link: '/discussions' },
              { icon: '📰', title: 'News Feed', desc: 'See the latest discussion activity across all amp categories — what the community is talking about right now', scrollTo: newsFeedRef },
              { icon: '🏆', title: 'Showcase', desc: 'Share photos of your rigs and see what others are playing', scrollTo: showcaseRef },
              { icon: '📄', title: 'Certify', desc: 'Generate branded PDF valuation certificates for insurance, sales, or records', scrollTo: certifyRef },
              { icon: '✏️', title: 'Contribute', desc: 'Suggest edits to amp descriptions — Pro users can earn trusted editor status for auto-approved edits', scrollTo: wikiRef },
              { icon: '⚡', title: 'Wiring Studio Beta', desc: 'Create amp schematics with an interactive drag-and-drop canvas.', betaNote: 'Beta is live now! We welcome your feedback — use the Feedback button to share ideas or report bugs. Phase 2 will add full wiring layout support.', link: '/wiring-studio', beta: true },
            ].map((item, i) => (
              <div key={i}
                onClick={() => {
                  if (item.link) navigate(item.link);
                  if (item.scrollTo?.current) {
                    item.scrollTo.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
                style={{
                  background: item.comingSoon ? 'linear-gradient(135deg, #fdf6ff 0%, #f0f0ff 100%)' : '#fafbfc',
                  borderRadius: '8px',
                  padding: '1.25rem',
                  textAlign: 'center',
                  border: item.comingSoon ? '1px solid #d8ccff' : '1px solid #e9ecef',
                  cursor: (item.link || item.scrollTo) ? 'pointer' : 'default',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
                onMouseEnter={e => {
                  if (item.link || item.scrollTo) {
                    e.currentTarget.style.borderColor = '#6c5ce7';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(108, 92, 231, 0.15)';
                  }
                }}
                onMouseLeave={e => {
                  if (item.link || item.scrollTo) {
                    e.currentTarget.style.borderColor = item.comingSoon ? '#d8ccff' : '#e9ecef';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                {item.comingSoon && (
                  <div style={{
                    position: 'absolute',
                    top: '0.5rem',
                    right: '0.5rem',
                    background: '#6c5ce7',
                    color: 'white',
                    fontSize: '0.65rem',
                    fontWeight: '700',
                    padding: '0.15rem 0.45rem',
                    borderRadius: '10px',
                    letterSpacing: '0.4px',
                    textTransform: 'uppercase'
                  }}>Coming Soon</div>
                )}
                {item.beta && (
                  <div style={{
                    position: 'absolute',
                    top: '0.5rem',
                    right: '0.5rem',
                    background: '#00b894',
                    color: 'white',
                    fontSize: '0.65rem',
                    fontWeight: '700',
                    padding: '0.15rem 0.45rem',
                    borderRadius: '10px',
                    letterSpacing: '0.4px',
                    textTransform: 'uppercase'
                  }}>Beta</div>
                )}
                <div style={{ fontSize: '1.75rem', marginBottom: '0.4rem' }}>{item.icon}</div>
                <div style={{ color: '#333', fontWeight: '600', fontSize: '0.95rem', marginBottom: '0.3rem' }}>{item.title}</div>
                <div style={{ color: '#888', fontSize: '0.85rem', lineHeight: '1.4' }}>{item.desc}</div>
                {item.betaNote && (
                  <div style={{ color: '#6c5ce7', fontSize: '0.8rem', lineHeight: '1.4', marginTop: '0.4rem', fontStyle: 'italic' }}>{item.betaNote}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Feature Comparison Matrix */}
        <div style={{
          background: 'white',
          borderRadius: '10px',
          padding: '2.5rem',
          border: '1px solid #e9ecef',
          marginBottom: '2rem'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#333',
            margin: '0 0 0.5rem 0',
            textAlign: 'center'
          }}>
            What Can You Do on AmpNebula?
          </h2>
          <p style={{
            color: '#888',
            fontSize: '0.9rem',
            textAlign: 'center',
            margin: '0 0 1.5rem 0'
          }}>
            No account needed to browse — create a free account to unlock collections, valuations, and community features.
          </p>

          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.9rem'
            }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                  <th style={{
                    textAlign: 'left',
                    padding: '0.75rem 1rem',
                    color: '#555',
                    fontWeight: '600',
                    minWidth: '240px'
                  }}>Feature</th>
                  <th style={{
                    textAlign: 'center',
                    padding: '0.75rem 0.5rem',
                    color: '#636e72',
                    fontWeight: '600',
                    width: '100px'
                  }}>
                    <div>Anonymous</div>
                    <div style={{ fontSize: '0.75rem', fontWeight: '400', color: '#999' }}>No account</div>
                  </th>
                  <th style={{
                    textAlign: 'center',
                    padding: '0.75rem 0.5rem',
                    color: '#00b894',
                    fontWeight: '600',
                    width: '100px'
                  }}>
                    <div>Free</div>
                    <div style={{ fontSize: '0.75rem', fontWeight: '400', color: '#999' }}>$0</div>
                  </th>
                  <th style={{
                    textAlign: 'center',
                    padding: '0.75rem 0.5rem',
                    color: '#6c5ce7',
                    fontWeight: '600',
                    width: '100px'
                  }}>
                    <div>Pro</div>
                    <div style={{ fontSize: '0.75rem', fontWeight: '400', color: '#999' }}>$1.99/mo</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {featureMatrix.map((section, si) => (
                  <React.Fragment key={si}>
                    <tr>
                      <td colSpan={4} style={{
                        padding: '0.6rem 1rem 0.4rem',
                        fontWeight: '700',
                        fontSize: '0.85rem',
                        color: '#2d1b69',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        borderTop: si > 0 ? '1px solid #e9ecef' : 'none',
                        background: '#fafbfc'
                      }}>
                        {section.category}
                      </td>
                    </tr>
                    {section.features.map((feature, fi) => (
                      <tr key={fi} style={{
                        borderBottom: '1px solid #f1f3f5'
                      }}>
                        <td style={{
                          padding: '0.5rem 1rem 0.5rem 1.5rem',
                          color: '#555'
                        }}>{feature.name}</td>
                        <td style={{ textAlign: 'center', padding: '0.5rem' }}>{renderCell(feature.anonymous)}</td>
                        <td style={{ textAlign: 'center', padding: '0.5rem' }}>{renderCell(feature.free)}</td>
                        <td style={{ textAlign: 'center', padding: '0.5rem' }}>{renderCell(feature.pro)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* AmpNebula Valuation Spotlight */}
        <div ref={valuationRef} style={{
          background: 'linear-gradient(135deg, #2d1b69 0%, #6c5ce7 100%)',
          borderRadius: '10px',
          padding: '2.5rem',
          marginBottom: '2rem',
          color: 'white'
        }}>
          <h2 style={{
            fontSize: '1.4rem',
            fontWeight: '600',
            margin: '0 0 0.75rem 0',
            textAlign: 'center'
          }}>
            AmpNebula Valuation™
          </h2>
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.7',
            textAlign: 'center',
            maxWidth: '750px',
            margin: '0 auto 1.25rem auto',
            opacity: 0.92
          }}>
            The first instant market valuation built specifically for guitar amplifiers. Using
            real-time web research and live marketplace data, every valuation includes
            fair market value, trade-in value, insurance replacement value, a confidence rating,
            and citations to comparable sales.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '0.75rem',
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            {[
              { label: 'Fair Market Value', desc: 'What it sells for privately' },
              { label: 'Trade-In Value', desc: 'What a dealer would offer' },
              { label: 'Insurance Value', desc: 'Replacement cost coverage' },
              { label: 'Comparable Sales', desc: 'Real listings cited as evidence' },
            ].map((item, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.12)',
                borderRadius: '8px',
                padding: '0.75rem',
                textAlign: 'center'
              }}>
                <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>{item.label}</div>
                <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '0.2rem' }}>{item.desc}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
            <button
              onClick={scrollToTop}
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                padding: '0.4rem 1rem',
                borderRadius: '6px',
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
            >
              ↑ Back to Top
            </button>
          </div>
        </div>

        {/* My Collection Panel */}
        <div
          ref={collectionsRef}
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: '2.5rem',
            border: '1px solid #e9ecef',
            marginBottom: '2rem'
          }}
        >
          <h2 style={{
            fontSize: '1.4rem',
            fontWeight: '600',
            color: '#333',
            margin: '0 0 0.75rem 0',
            textAlign: 'center'
          }}>
            My Collection
          </h2>
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.7',
            color: '#555',
            textAlign: 'center',
            maxWidth: '750px',
            margin: '0 auto 1.5rem auto'
          }}>
            Keep track of every amp you own or wantlist. Log serial numbers, purchase prices, and
            dates; upload your own photos; and see the total estimated value of your gear at a glance.
            Free accounts can track up to 3 amps — <strong>Pro</strong> users get unlimited collection entries.
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            {[
              { icon: '📋', label: 'Own & Want lists' },
              { icon: '🔢', label: 'Serial numbers & dates' },
              { icon: '📸', label: 'Personal photos' },
              { icon: '💵', label: 'Value dashboard' },
              { icon: '🔗', label: 'Shareable public link' },
            ].map((item, i) => (
              <div key={i} style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                padding: '0.6rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem',
                border: '1px solid #e9ecef',
                fontSize: '0.88rem',
                color: '#444'
              }}>
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center' }}>
            <img
              src="/Collection1.png"
              alt="My Collection feature screenshot"
              style={{
                width: '100%',
                maxWidth: '820px',
                height: 'auto',
                borderRadius: '10px',
                border: '1px solid #e9ecef',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}
            />
          </div>
          <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
            <button
              onClick={scrollToTop}
              style={{
                background: '#f8f9fa',
                border: '1px solid #e9ecef',
                color: '#6c5ce7',
                padding: '0.4rem 1rem',
                borderRadius: '6px',
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'background 0.2s, border-color 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f1f3f5'; e.currentTarget.style.borderColor = '#6c5ce7'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fa'; e.currentTarget.style.borderColor = '#e9ecef'; }}
            >
              ↑ Back to Top
            </button>
          </div>
        </div>

        {/* Showcase Panel */}
        <div
          ref={showcaseRef}
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: '2.5rem',
            border: '1px solid #e9ecef',
            marginBottom: '2rem'
          }}
        >
          <h2 style={{
            fontSize: '1.4rem',
            fontWeight: '600',
            color: '#333',
            margin: '0 0 0.75rem 0',
            textAlign: 'center'
          }}>
            Showcase
          </h2>
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.7',
            color: '#555',
            textAlign: 'center',
            maxWidth: '750px',
            margin: '0 auto 1.5rem auto'
          }}>
            Show off your rig to the AmpNebula community. Post photos of your setup, browse what
            other players and collectors are running, and like &amp; comment on their gear. Anyone
            can browse the Showcase — a free account is all you need to post. You can also share
            your public collection link anywhere on social media to let friends, buyers, or
            followers see exactly what's in your arsenal.
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            {[
              { icon: '📷', label: 'Post your rig' },
              { icon: '👀', label: 'Browse community gear' },
              { icon: '❤️', label: 'Like & comment' },
              { icon: '🎸', label: 'Guitars, amps &amp; more' },
            ].map((item, i) => (
              <div key={i} style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                padding: '0.6rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem',
                border: '1px solid #e9ecef',
                fontSize: '0.88rem',
                color: '#444'
              }}>
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center' }}>
            <img
              src="/Showcase1.jpg"
              alt="Showcase feature screenshot"
              style={{
                width: '100%',
                maxWidth: '820px',
                height: 'auto',
                borderRadius: '10px',
                border: '1px solid #e9ecef',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}
            />
          </div>
          <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
            <button
              onClick={scrollToTop}
              style={{
                background: '#f8f9fa',
                border: '1px solid #e9ecef',
                color: '#6c5ce7',
                padding: '0.4rem 1rem',
                borderRadius: '6px',
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'background 0.2s, border-color 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f1f3f5'; e.currentTarget.style.borderColor = '#6c5ce7'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fa'; e.currentTarget.style.borderColor = '#e9ecef'; }}
            >
              ↑ Back to Top
            </button>
          </div>
        </div>

        {/* News Feed Panel */}
        <div
          ref={newsFeedRef}
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: '2.5rem',
            border: '1px solid #e9ecef',
            marginBottom: '2rem'
          }}
        >
          <h2 style={{
            fontSize: '1.4rem',
            fontWeight: '600',
            color: '#333',
            margin: '0 0 0.75rem 0',
            textAlign: 'center'
          }}>
            📰 Community News Feed
          </h2>
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.7',
            color: '#555',
            textAlign: 'center',
            maxWidth: '750px',
            margin: '0 auto 1.5rem auto'
          }}>
            The News Feed gives you a rolling view of the most recent discussion activity across
            the entire AmpNebula community — no login required. See which amp topics are heating
            up, jump straight into a thread, and stay connected to what collectors and players
            are talking about right now. Registered users can react to posts directly from the feed.
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            {[
              { icon: '🔔', label: 'Latest activity, first' },
              { icon: '🎸', label: 'Covers all amp categories' },
              { icon: '👍', label: 'React to posts' },
              { icon: '💬', label: 'Jump straight into threads' },
              { icon: '🌐', label: 'Public — no login needed' },
            ].map((item, i) => (
              <div key={i} style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                padding: '0.6rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem',
                border: '1px solid #e9ecef',
                fontSize: '0.88rem',
                color: '#444'
              }}>
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
            <button
              onClick={() => navigate('/news-feed')}
              style={{
                background: 'linear-gradient(135deg, #2d1b69 0%, #11998e 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.65rem 1.75rem',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              Browse the News Feed →
            </button>
          </div>
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={scrollToTop}
              style={{
                background: '#f8f9fa',
                border: '1px solid #e9ecef',
                color: '#6c5ce7',
                padding: '0.4rem 1rem',
                borderRadius: '6px',
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'background 0.2s, border-color 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f1f3f5'; e.currentTarget.style.borderColor = '#6c5ce7'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fa'; e.currentTarget.style.borderColor = '#e9ecef'; }}
            >
              ↑ Back to Top
            </button>
          </div>
        </div>

        {/* PDF Valuation Certificate Panel */}
        <div
          ref={certifyRef}
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: '2.5rem',
            border: '1px solid #e9ecef',
            marginBottom: '2rem'
          }}
        >
          <h2 style={{
            fontSize: '1.4rem',
            fontWeight: '600',
            color: '#333',
            margin: '0 0 0.75rem 0',
            textAlign: 'center'
          }}>
            PDF Valuation Certificate
          </h2>
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.7',
            color: '#555',
            textAlign: 'center',
            maxWidth: '750px',
            margin: '0 auto 1.5rem auto'
          }}>
            After running an AmpNebula Valuation™, <strong>Pro</strong> subscribers can generate a
            branded PDF certificate documenting the amp's fair market, trade-in, and insurance
            replacement values — complete with comparable sales citations. Perfect for insurance
            records, private sales, estate documentation, or just knowing what your gear is worth
            on paper.
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            {[
              { icon: '📋', label: 'Fair market value' },
              { icon: '🔄', label: 'Trade-in value' },
              { icon: '🛡️', label: 'Insurance replacement value' },
              { icon: '🔗', label: 'Comparable sales cited' },
              { icon: '📥', label: 'Instant PDF download' },
            ].map((item, i) => (
              <div key={i} style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                padding: '0.6rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem',
                border: '1px solid #e9ecef',
                fontSize: '0.88rem',
                color: '#444'
              }}>
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center' }}>
            <img
              src="/Certificate1.jpg"
              alt="AmpNebula Valuation Certificate example"
              style={{
                width: '100%',
                maxWidth: '820px',
                height: 'auto',
                borderRadius: '10px',
                border: '1px solid #e9ecef',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}
            />
          </div>
          <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
            <button
              onClick={scrollToTop}
              style={{
                background: '#f8f9fa',
                border: '1px solid #e9ecef',
                color: '#6c5ce7',
                padding: '0.4rem 1rem',
                borderRadius: '6px',
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'background 0.2s, border-color 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f1f3f5'; e.currentTarget.style.borderColor = '#6c5ce7'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fa'; e.currentTarget.style.borderColor = '#e9ecef'; }}
            >
              ↑ Back to Top
            </button>
          </div>
        </div>

        {/* Wiki / Contribute Panel */}
        <div
          ref={wikiRef}
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: '2.5rem',
            border: '1px solid #e9ecef',
            marginBottom: '2rem'
          }}
        >
          <h2 style={{
            fontSize: '1.4rem',
            fontWeight: '600',
            color: '#333',
            margin: '0 0 0.75rem 0',
            textAlign: 'center'
          }}>
            ✏️ Community-Driven Wiki
          </h2>
          <p style={{
            color: '#555',
            fontSize: '1rem',
            lineHeight: '1.7',
            textAlign: 'center',
            maxWidth: '750px',
            margin: '0 auto 1.25rem auto'
          }}>
            AmpNebula is the <strong>Wikipedia for amp gear heads</strong>. Every amplifier page can be edited and improved
            by registered users. Found an error? Know more about an amp's history? Add it! Your contributions
            help build the most accurate and comprehensive amp encyclopedia on the web.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '0.75rem',
            maxWidth: '800px',
            margin: '0 auto 1.5rem auto'
          }}>
            {[
              { icon: '📝', label: 'Edit Any Amp', desc: 'Suggest changes to descriptions, specs, and history' },
              { icon: '👀', label: 'Track Changes', desc: 'See exactly what changed with diff-style review' },
              { icon: '✅', label: 'Moderated Quality', desc: 'Edits are reviewed to maintain accuracy' },
              { icon: '⭐', label: 'Trusted Editors', desc: 'Pro users can earn auto-approval status' },
            ].map((item, i) => (
              <div key={i} style={{
                background: '#fafbfc',
                borderRadius: '8px',
                padding: '1rem',
                textAlign: 'center',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>{item.icon}</div>
                <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#333', marginBottom: '0.2rem' }}>{item.label}</div>
                <div style={{ fontSize: '0.8rem', color: '#888', lineHeight: '1.4' }}>{item.desc}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center' }}>
            <img
              src="/Wiki1.png"
              alt="AmpNebula Wiki editing interface"
              style={{
                width: '100%',
                maxWidth: '820px',
                height: 'auto',
                borderRadius: '10px',
                border: '1px solid #e9ecef',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}
            />
          </div>
          <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
            <button
              onClick={scrollToTop}
              style={{
                background: '#f8f9fa',
                border: '1px solid #e9ecef',
                color: '#6c5ce7',
                padding: '0.4rem 1rem',
                borderRadius: '6px',
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'background 0.2s, border-color 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f1f3f5'; e.currentTarget.style.borderColor = '#6c5ce7'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fa'; e.currentTarget.style.borderColor = '#e9ecef'; }}
            >
              ↑ Back to Top
            </button>
          </div>
        </div>

        {/* Wiring Studio Panel */}
        <div
          ref={wiringStudioRef}
          style={{
            background: 'linear-gradient(135deg, #1a0533 0%, #2d1b69 60%, #4a2090 100%)',
            borderRadius: '10px',
            padding: '2.5rem',
            marginBottom: '2rem',
            color: 'white'
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
            <span style={{
              background: 'rgba(0, 212, 170, 0.25)',
              border: '1px solid rgba(0, 212, 170, 0.6)',
              color: '#00d4aa',
              fontSize: '0.72rem',
              fontWeight: '700',
              padding: '0.2rem 0.6rem',
              borderRadius: '10px',
              letterSpacing: '0.6px',
              textTransform: 'uppercase'
            }}>Beta — Now Live</span>
          </div>
          <h2 style={{
            fontSize: '1.4rem',
            fontWeight: '600',
            margin: '0.5rem 0 0.75rem 0',
            textAlign: 'center'
          }}>
            ⚡ Wiring Studio
          </h2>
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.7',
            textAlign: 'center',
            maxWidth: '750px',
            margin: '0 auto 1.25rem auto',
            opacity: 0.92
          }}>
            <strong style={{ color: '#00d4aa' }}>Wiring Studio Beta is now live!</strong> Create circuit schematics
            with an interactive drag-and-drop canvas — place tubes, transformers, capacitors, resistors, and more,
            connect them with smart-routed wires, and save your work. Whether you're documenting a vintage circuit
            or planning a custom build, Wiring Studio gives you a purpose-built canvas for amp electronics.
          </p>
          <p style={{
            fontSize: '0.95rem',
            lineHeight: '1.7',
            textAlign: 'center',
            maxWidth: '700px',
            margin: '0 auto 1.25rem auto',
            opacity: 0.85
          }}>
            We'd love your feedback! Hit the <strong style={{ color: 'white' }}>Feedback</strong> button to share
            an idea, report a bug, or tell us what you'd like to see next. If you'd like us to follow up
            with you about our plans for a feature or fix you've submitted, don't forget to include your
            email address in the feedback form.
          </p>
          <p style={{
            fontSize: '0.9rem',
            lineHeight: '1.6',
            textAlign: 'center',
            maxWidth: '650px',
            margin: '0 auto 1.25rem auto',
            opacity: 0.75,
            fontStyle: 'italic'
          }}>
            Phase 2 of the Beta will expand Wiring Studio to support full wiring layouts in addition to schematics.
          </p>
          <div style={{ textAlign: 'center', margin: '0 auto 1.75rem auto', maxWidth: '860px' }}>
            <img
              src="/Wiring Studio 1.jpg"
              alt="Wiring Studio — interactive amp schematic canvas"
              style={{
                width: '100%',
                borderRadius: '10px',
                border: '2px solid rgba(255,255,255,0.2)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
                display: 'block'
              }}
            />
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '0.75rem',
            maxWidth: '760px',
            margin: '0 auto 1.5rem auto'
          }}>
            {[
              { label: 'Drag-and-Drop Canvas', desc: 'Place and connect components freely' },
              { label: 'Rich Component Library', desc: 'Tubes, transformers, caps, resistors & more' },
              { label: 'Smart Wire Routing', desc: 'Auto-routed orthogonal wires with jump arcs' },
              { label: 'Pro: Export to PNG/SVG/PDF', desc: 'Save a clean image or document' },
            ].map((item, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '8px',
                padding: '0.75rem',
                textAlign: 'center',
                border: '1px solid rgba(255,255,255,0.15)'
              }}>
                <div style={{ fontWeight: '600', fontSize: '0.88rem', marginBottom: '0.25rem' }}>{item.label}</div>
                <div style={{ fontSize: '0.78rem', opacity: 0.7, lineHeight: '1.4' }}>{item.desc}</div>
              </div>
            ))}
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '8px',
            padding: '1rem 1.5rem',
            maxWidth: '560px',
            margin: '0 auto 1.5rem auto',
            textAlign: 'center'
          }}>
            <div style={{ fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.4rem' }}>Tier Access</div>
            <div style={{ fontSize: '0.85rem', opacity: 0.85, lineHeight: '1.7' }}>
              <span style={{ color: '#00d4aa', fontWeight: '600' }}>Free</span>: Try it out — save up to <strong style={{ color: 'white' }}>1 diagram</strong><br />
              <span style={{ color: '#a29bfe', fontWeight: '600' }}>Pro</span>: <strong style={{ color: 'white' }}>Unlimited</strong> diagrams + PNG / SVG / PDF export
            </div>
          </div>
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <button
              onClick={() => navigate('/wiring-studio')}
              style={{
                background: 'linear-gradient(135deg, #00b894 0%, #00d4aa 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.7rem 2rem',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'opacity 0.2s',
                marginRight: '0.75rem'
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              Try Wiring Studio →
            </button>
            <button
              onClick={scrollToTop}
              style={{
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.25)',
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
            >
              ↑ Back to Top
            </button>
          </div>
        </div>

        {/* CTA Section */}
        {!isAuthenticated ? (
          <div style={{
            background: 'white',
            borderRadius: '10px',
            padding: '2rem',
            border: '2px solid #00b894',
            textAlign: 'center'
          }}>
            <h3 style={{
              color: '#333',
              fontSize: '1.25rem',
              fontWeight: '600',
              margin: '0 0 0.75rem 0'
            }}>
              Ready to get started?
            </h3>
            <p style={{
              color: '#555',
              fontSize: '1rem',
              margin: '0 0 0.25rem 0',
              lineHeight: '1.6'
            }}>
              Create a free account to start building your collection, run your first AmpNebula Valuation™,
              and join the community. No credit card required.
            </p>
            <p style={{
              color: '#999',
              fontSize: '0.85rem',
              margin: 0
            }}>
              Already have an account? Log in from the menu above.
            </p>
          </div>
        ) : (() => {
          const tier = user?.subscription_tier || 'free';
          const config = tierConfig[tier] || tierConfig.free;
          const isFree = tier === 'free';
          return (
            <div style={{
              background: 'white',
              borderRadius: '10px',
              padding: '2rem',
              border: `2px solid ${config.color}`,
              textAlign: 'center'
            }}>
              <div style={{
                display: 'inline-block',
                background: config.color,
                color: 'white',
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                fontSize: '0.8rem',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '0.75rem'
              }}>
                {config.label} Tier
              </div>
              <h3 style={{
                color: '#333',
                fontSize: '1.25rem',
                fontWeight: '600',
                margin: '0 0 1rem 0'
              }}>
                You're on the {config.label} plan
              </h3>
              <div style={{
                textAlign: 'left',
                maxWidth: '400px',
                margin: '0 auto 1.25rem auto'
              }}>
                {config.highlights.map((item, i) => (
                  <div key={i} style={{
                    padding: '0.3rem 0',
                    color: '#555',
                    fontSize: '0.95rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem'
                  }}>
                    <span style={{ color: config.color, fontWeight: '700', flexShrink: 0 }}>✓</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              {isFree && config.upgradeNote && (
                <>
                  <p style={{
                    color: '#888',
                    fontSize: '0.9rem',
                    margin: '0 0 1rem 0',
                    lineHeight: '1.5'
                  }}>
                    {config.upgradeNote}
                  </p>
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    style={{
                      background: 'linear-gradient(135deg, #6c5ce7, #a29bfe)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.7rem 2rem',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={e => e.target.style.opacity = '0.9'}
                    onMouseLeave={e => e.target.style.opacity = '1'}
                  >
                    Upgrade to Pro
                  </button>
                </>
              )}
            </div>
          );
        })()}

        <UpgradeModal
          show={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          message="Unlock unlimited collection tracking, unlimited valuations, PDF certificates, and more."
          tier="pro"
        />

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          padding: '1.5rem 0 0.5rem',
          color: '#aaa',
          fontSize: '0.8rem'
        }}>
          AmpNebula is a product of QorVertex LLC &middot; © {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
};

export default PublicInfoScreen;
